import { useState } from 'react'
import { api } from '../lib/api'
import { isWeChat } from '../lib/env'
import { formatBytes } from '../lib/format'
import type { Me } from '../lib/types'
import { useToast } from './Toast'

interface Props {
  me: Me
  onLogout: () => void
  onFamilyChange: () => void
}

export default function MePage({ me, onLogout, onFamilyChange }: Props) {
  const toast = useToast()
  const [revokingId, setRevokingId] = useState<string | null>(null)
  const [renaming, setRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState(me.family.name)
  const [savingRename, setSavingRename] = useState(false)

  const isOwner = me.device.role === 'owner'
  const photoUsage = me.r2_usage_bytes
  const photoLimitMb = 10000
  const photoPercent = (photoUsage / (photoLimitMb * 1024 * 1024)) * 100

  async function revoke(id: string) {
    if (!confirm('确定撤销这台设备的访问？')) return
    setRevokingId(id)
    try {
      await api.revokeDevice(id)
      toast.show('已撤销')
      onFamilyChange()
    } catch (e) {
      toast.show(e instanceof Error ? e.message : '撤销失败')
    } finally {
      setRevokingId(null)
    }
  }

  async function saveRename() {
    const v = renameValue.trim()
    if (!v) {
      setRenameValue(me.family.name)
      setRenaming(false)
      return
    }
    if (v === me.family.name) {
      setRenaming(false)
      return
    }
    setSavingRename(true)
    try {
      await api.renameFamily(v)
      toast.show('已更新')
      setRenaming(false)
      onFamilyChange()
    } catch (e) {
      toast.show(e instanceof Error ? e.message : '更新失败')
    } finally {
      setSavingRename(false)
    }
  }

  function cancelRename() {
    setRenameValue(me.family.name)
    setRenaming(false)
  }

  async function exportData() {
    try {
      const data = await api.exportAll()
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `baby-log-${data.family.id}-${new Date().toISOString().slice(0, 10)}.json`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      toast.show(e instanceof Error ? e.message : '导出失败')
    }
  }

  return (
    <main className="me-page">
      <section className="settings-section">
        <h3>家庭</h3>
        {renaming ? (
          <div className="rename-row">
            <input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              maxLength={30}
              autoFocus
              disabled={savingRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveRename()
                else if (e.key === 'Escape') cancelRename()
              }}
            />
            <button
              className="btn-primary btn-sm"
              onClick={saveRename}
              disabled={savingRename || !renameValue.trim()}
            >
              {savingRename ? '保存中' : '保存'}
            </button>
            <button
              className="btn-secondary btn-sm"
              onClick={cancelRename}
              disabled={savingRename}
            >
              取消
            </button>
          </div>
        ) : (
          <div className="info-row">
            <span>名称</span>
            <span className="info-value" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {me.family.name}
              {isOwner && (
                <button
                  onClick={() => {
                    setRenameValue(me.family.name)
                    setRenaming(true)
                  }}
                  style={{
                    background: 'none',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    padding: '2px 8px',
                    fontSize: 11,
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                  }}
                  title="修改名称"
                >
                  ✏️ 修改
                </button>
              )}
            </span>
          </div>
        )}
        {me.family.join_code && (
          <div className="info-row">
            <span>家庭代码</span>
            <span className="info-value" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', letterSpacing: 2, fontWeight: 700 }}>
                {me.family.join_code}
              </span>
              <button
                onClick={() => {
                  if (me.family.join_code) {
                    navigator.clipboard?.writeText(me.family.join_code)
                    toast.show('已复制')
                  }
                }}
                style={{
                  background: 'none',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  padding: '2px 8px',
                  fontSize: 11,
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                }}
                title="复制代码"
              >
                📋
              </button>
            </span>
          </div>
        )}
        <div className="info-row">
          <span>我的角色</span>
          <span className="info-value">
            {isOwner ? '管理员' : '成员'}
          </span>
        </div>
        {me.photos_enabled ? (
          <>
            <div className="info-row">
              <span>照片用量</span>
              <span className="info-value">
                {formatBytes(photoUsage)} / 10 GB
              </span>
            </div>
            <div className="progress-bar">
              <div
                className={`progress-fill ${photoPercent > 80 ? 'warn' : ''}`}
                style={{ width: `${Math.min(100, photoPercent)}%` }}
              />
            </div>
          </>
        ) : (
          <div className="info-row">
            <span>照片</span>
            <span className="info-value">未启用（需 R2）</span>
          </div>
        )}
      </section>

      {isOwner && (
        <section className="settings-section">
          <h3>设备 ({me.devices.filter((d) => !d.revoked_at).length})</h3>
          <ul className="devices-list">
            {me.devices
              .filter((d) => !d.revoked_at)
              .map((d) => (
                <li key={d.id} className="device-item">
                  <div>
                    <div>
                      {d.name}
                      {d.id === me.device.id && <span className="device-self"> · 本机</span>}
                    </div>
                    <div className="device-role">
                      {d.relationship === 'father' ? '爸爸' : d.relationship === 'mother' ? '妈妈' : d.relationship === 'other' ? '其他' : ''}
                      {d.role === 'owner' ? ' · 管理员' : ' · 成员'}
                      {d.last_seen_at
                        ? ` · 最近 ${new Date(d.last_seen_at).toLocaleDateString('zh-CN')}`
                        : ''}
                    </div>
                  </div>
                  {d.id !== me.device.id && (
                    <button
                      className="device-revoke"
                      onClick={() => revoke(d.id)}
                      disabled={revokingId === d.id}
                    >
                      {revokingId === d.id ? '撤销中' : '撤销'}
                    </button>
                  )}
                </li>
              ))}
          </ul>
        </section>
      )}

      {isOwner && (
        <section className="settings-section">
          <h3>AI 辅助</h3>
          <div className={`ai-status ${me.ai_configured ? 'enabled' : 'disabled'}`}>
            文本模型：{me.ai_configured ? '✅ 已配置' : '❌ 未配置'}
          </div>
          {(() => {
            const vision = me.ai_vision_configured
            return (
              <div
                className={`ai-status ${vision ? 'enabled' : 'disabled'}`}
                style={{ marginTop: 6 }}
              >
                视觉模型：{vision ? '✅ 已配置' : '❌ 未配置'}
              </div>
            )
          })()}
          <p className="settings-hint">AI 默认关闭且仅手动触发。</p>
        </section>
      )}

      <section className="settings-section">
        <h3>数据</h3>
        <button className="btn-secondary" onClick={exportData}>
          导出全部记录
        </button>
        <p className="settings-hint">导出为 JSON 文件，可备份到本地。</p>
      </section>

      <section className="settings-section">
        <h3>环境</h3>
        <div className="info-row">
          <span>访问方式</span>
          <span className="info-value">{isWeChat() ? '微信内' : '浏览器'}</span>
        </div>
        <p className="settings-hint">宝宝记录 v0.1 · Cloudflare 全托管</p>
      </section>

      <section className="settings-section">
        <button
          className="text-danger"
          onClick={() => {
            if (confirm('确定退出当前家庭？本机的本地数据会清除，但家庭记录保留。')) {
              onLogout()
            }
          }}
        >
          退出当前家庭
        </button>
      </section>
    </main>
  )
}
